import React, { useEffect, useRef, useState } from 'react';
import DataGrid, { Column, Editing, Selection, Summary, TotalItem } from 'devextreme-react/data-grid';
import { Button } from 'devextreme-react/button';
import {
    getReturnToMayaveProductSummariesByVoucher,
    updateReturnToMayaveProduct,
    updateReturnToMayaveProductPricesBy
} from '../services/return-to-mayave-request.service';
import notistack from 'modules/core/notistack';
import { HttpStatus } from 'modules/core';
import { Modal } from 'react-bootstrap';

interface ShowReturnToMayaveStoreModalProps {
    selectedId: string;
    status: string;
    onClose: () => void;
    onSave: () => void;
}

const ShowReturnToMayaveStoreModal: React.FC<ShowReturnToMayaveStoreModalProps> = ({
    selectedId,
    status,
    onClose,
    onSave
}) => {
    const dataGridRef = useRef<DataGrid>(null);
    const [gridData, setGridData] = useState<any[]>([]);
    const isMainStore = localStorage.getItem('mayavePurityEdit') !== 'false' && status.toLowerCase() === 'Pending'.toLowerCase();
    const isMainStoreTocheck = localStorage.getItem('mayavePurityEdit');
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const [showConfirm, setShowConfirm] = useState(false);
    const [mayavePrice, setMayavePrice] = useState(0)
    const [showPriceConfirm, setShowPriceConfirm] = useState(false)
    const [originalMayavePrices, setOriginalMayavePrices] = useState<Record<number, number>>({});

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await getReturnToMayaveProductSummariesByVoucher(selectedId);
                const data = response?.data || [];

                if (Array.isArray(data) && data.length > 0) {
                    setGridData(data);
                    const originalMap: Record<number, number> = {};
                    data.forEach(row => {
                        originalMap[row.id] = row.mayavePrice ?? 0;
                    });
                    setOriginalMayavePrices(originalMap);
                }
            } catch (error) {
                console.error('Failed to load data:', error);
                setGridData([]);
            }
        };

        fetchData();
    }, [selectedId]);


    const cellTemplate = (cellElement: HTMLElement, cellInfo: any) => {
        if (!isMainStore) {
            cellElement.innerText = cellInfo.text;
            return;
        }

        const input = document.createElement('input');
        input.type = 'number';
        input.className = 'form-control';
        input.style.width = '100%';
        input.step = '0.01';
        input.min = "0";
        input.value = cellInfo.value ?? '0';

        const rowId = cellInfo.data?.id;
        const originalValue = originalMayavePrices[rowId] ?? 0;

        if (originalValue > 0) {
            input.disabled = true;
            setMayavePrice(originalValue);
        }

        input.addEventListener('wheel', (e) => {
            e.preventDefault();
        });

        input.addEventListener('keydown', (e: any) => {
            if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E') {
                e.preventDefault();
            }
        });

        input.addEventListener('input', () => {
            const regex = /^\d*\.?\d*$/;

            if (!regex.test(input.value)) {
                input.value = input.value.slice(0, -1);
            }
        });

        input.addEventListener('paste', (e: ClipboardEvent) => {
            const pasted = e.clipboardData?.getData('text') || '';
            const regex = /^\d+(\.\d+)?$/;

            if (!regex.test(pasted)) {
                e.preventDefault();
            }
        });

        input.addEventListener('change', () => {
            const newValue = parseFloat(input.value ?? 0);

            setGridData(prevData =>
                prevData.map(item =>
                    item.id === cellInfo.data.id
                        ? { ...item, mayavePrice: newValue }
                        : item
                )
            );
        });

        cellElement.appendChild(input);
    };

    const handleSubmit = async () => {
        const invalid = selectedRows.find((row) => {
            const updated = gridData.find((item) => item.id === row.id);
            return !updated || Number(updated?.mayavePrice ?? 0) <= 0;
        });

        if (invalid) {
            notistack.error("Mayave price must be greater than zero.");
            return;
        }

        try {
            const payload = {
                ReturnToMayaveProductPricesUpdateList: selectedRows.map((row) => {
                    const updated = gridData.find((item) => item.id === row.id);
                    return {
                        id: updated?.id,
                        mayavePrice: updated?.mayavePrice ?? 0,
                    };
                }),
            };

            const response = await updateReturnToMayaveProductPricesBy(payload);

            if (response.status === HttpStatus.Success) {
                notistack.notifyFromApi(response);
                onSave();
            } else {
                notistack.notifyFromApi(response);
            }
        } catch (error: any) {
            notistack.notifyFromApi(error?.message || "Submit failed.");
        }
    };


    const handleAcceptConfirm = async () => {
        const payload = {
            voucherNo: selectedId,
            ReturnToMayaveProductIds: selectedRows.length > 0
                ? selectedRows.map(row => row.id)
                : []
        };

        try {
            const response = await updateReturnToMayaveProduct(payload);

            if (response.status === HttpStatus.Success) {
                notistack.notifyFromApi(response);
                onSave();
            } else {
                notistack.notifyFromApi(response);
            }
        } catch (error: any) {
            notistack.notifyFromApi(error?.message || "Submission failed.");
        }
    };

    const anyRowWithChangedPrice = selectedRows.some(row => {
        const updated = gridData.find(item => item.id === row.id);
        const original = originalMayavePrices[row.id] ?? 0;
        return Number(updated?.mayavePrice) !== Number(original);
    });

    return (
        <>
            <DataGrid
                dataSource={gridData}
                keyExpr="id"
                ref={dataGridRef}
                showBorders
                columnAutoWidth={true}
                onRowPrepared={(e) => {
                    if (e.rowType === 'data' && e.data.status?.toLowerCase() === 'accepted') {
                        e.rowElement.classList.add('no-selection-checkbox');
                    }
                }}
                onSelectionChanged={(e) => {
                    const filteredSelection = e.selectedRowsData.filter(
                        row => row.status?.toLowerCase() !== 'accepted'
                    );
                    if (filteredSelection.length !== e.selectedRowsData.length) {
                        dataGridRef.current?.instance.selectRows(filteredSelection.map(r => r.id), false);
                    }
                    setSelectedRows(filteredSelection);
                }}
            >
                <Editing mode="cell" allowUpdating={true} />
                <Selection mode="multiple" />
                <Column dataField="voucherNo" caption="Voucher No" dataType="string" allowEditing={false} />
                <Column dataField="materialType" caption="Material Type" dataType="string" allowEditing={false} />
                <Column dataField="huid" caption="HUID" dataType="string" allowEditing={false} />
                <Column dataField="certificateNo" caption="Certificate No" dataType="string" allowEditing={false} />
                <Column dataField="ordNo" caption="Order No" dataType="string" allowEditing={false} />
                <Column dataField="dpPrice" format="#,##0.00" caption="DP Price" dataType="number" allowEditing={false} />
                <Column
                    dataField="mayavePrice"
                    caption="Mayave Price"
                    format="#,##0.00"
                    cellTemplate={cellTemplate}
                    allowEditing={false}
                />
                <Column
                    dataField="status"
                    caption="Status"
                    dataType="string"
                    allowEditing={false}
                />
            </DataGrid>


            <div className="d-flex justify-content-between mt-3 gap-2">
                <div className="mt-2 text-danger text-satrt">
                    {isMainStoreTocheck == 'false' && <><strong>Note:</strong> If a product is not selected, it will be marked as rejected. </>}

                </div>
                {isMainStore ? (
                    anyRowWithChangedPrice && <Button text="Submit" className="submit-btn" onClick={() => setShowPriceConfirm(true)} />
                ) : (
                    <>
                        {(status.toLowerCase() == 'Inprogress'.toLowerCase() && isMainStoreTocheck == 'false') && <Button
                            text="Submit"
                            className="submit-btn btn-success"
                            onClick={() => {
                                setShowConfirm(true);
                            }
                            }
                        />}
                    </>
                )}
            </div>

            <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
                <Modal.Header closeButton >
                    <Modal.Title>Confirmation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to accept these prices?
                </Modal.Body>
                <Modal.Footer className='d-flex justify-content-between'>
                    <div className="mt-2 text-danger text-satrt">
                        <strong>Note:</strong> If a product is not selected, it will be marked as rejected.
                    </div>
                    <Button
                        className='btn submit-btn'
                        onClick={handleAcceptConfirm}
                    >
                        Confirm
                    </Button>

                </Modal.Footer>

            </Modal>

            <Modal show={showPriceConfirm} onHide={() => setShowPriceConfirm(false)} centered>
                <Modal.Header closeButton >
                    <Modal.Title>Confirmation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to change price?
                </Modal.Body>
                <Modal.Footer className='d-flex justify-content-between'>
                    <Button
                        className='btn submit-btn'
                        onClick={handleSubmit}
                    >
                        Confirm
                    </Button>

                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ShowReturnToMayaveStoreModal;